import { AsperaError } from '../classes/AsperaError';
import { of, zip, mergeMap } from 'rxjs';
import { delay } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { getTransferSystemStatus, runningWithAsperaMobile, sendMobileSdkMessage } from './transfer';
import { language } from '../services/LanguageService';
import { deepCloneNative, getEnvironmentVar, getParamString, getQueryParams, isIOS, isMobile, safeEncode } from './helpers';
import { coreHelpUrls, storageKeys } from '../constants/constants';
import { tracking } from '../services/TrackingService';
import { Axios } from '../classes/Axios';
import MarkdownIt from 'markdown-it';
const windowOpenSafe = 'noopener,noreferrer';
/**
 * Safely get localStorage item
 *
 * @param key - localStorage key that references data to return
 */
export const getStorageItem = (key) => {
    if (localStorage) {
        return localStorage.getItem(key);
    }
    else {
        new AsperaError('getStorageItem: localStorage is not available');
        return null;
    }
};
/**
 * Safely get localStorage item that is expected to be Object or Array
 *
 * @param key - localStorage key that references data to return
 *
 * @returns an object or array (depending on what is in storage) or null if not JSON or unable to parse
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getJsonStorageItem = (key) => {
    try {
        const foundItem = getStorageItem(key);
        if ((foundItem === null || foundItem === void 0 ? void 0 : foundItem.indexOf('{')) === 0 || (foundItem === null || foundItem === void 0 ? void 0 : foundItem.indexOf('[')) === 0) {
            return JSON.parse(foundItem);
        }
        else {
            return null;
        }
    }
    catch (error) {
        new AsperaError('getJsonStorageItem: unable to parse JSON item from storage', error);
        return null;
    }
};
/**
 * Safely set localStorage item
 */
export const setStorageItem = (key, value) => {
    if (localStorage) {
        localStorage.setItem(key, value);
    }
    else {
        new AsperaError('setStorageItem: localStorage is not available');
    }
};
/**
 * Safely set localStorage item that is an Object or Array
 *
 * @param key - localStorage key that references data to return
 *
 * @returns an object or array (depending on what is in storage) or null if not JSON or unable to parse
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setJsonStorageItem = (key, value) => {
    try {
        const stringOfItem = JSON.stringify(value);
        setStorageItem(key, stringOfItem);
    }
    catch (error) {
        new AsperaError('setJsonStorageItem: unable to parse JSON item for storage', error);
    }
};
/**
 * Safely remove localStorage item
 */
export const removeStorageItem = (key) => {
    if (localStorage) {
        localStorage.removeItem(key);
    }
    else {
        new AsperaError('removeStorageItem: localStorage is not available');
    }
};
/**
 * Safely clear all localStorage items
 */
export const clearStorageItems = () => {
    if (localStorage) {
        localStorage.clear();
    }
    else {
        new AsperaError('clearStorageItems: localStorage is not available');
    }
};
/**
 * Open a URL in a new browser tab or window
 *
 * @param url - The URL or relative path to open in a new window or tab
 * @param external - Indicate if URL is external (not to same app)
 */
export const openNewTab = (url, external) => {
    const prefix = getEnvironmentVar('route_prefix');
    let finalRoute = url;
    if (!(external || !prefix)) {
        url = url[0] === '/' ? url : '/' + url;
        finalRoute = prefix + url;
    }
    if (runningWithAsperaMobile()) {
        sendMobileSdkMessage({
            purpose: 'OPEN_URL',
            data: finalRoute,
        });
    }
    else if (typeof window.asperaOpenNewTab === 'function') {
        window.asperaOpenNewTab(finalRoute);
    }
    else {
        window.open(finalRoute, '_blank', windowOpenSafe);
    }
};
/**
 * Take a query selector (only works off of single item, so use ID or unique class) and check if the child is inside of it.
 * Useful for testing if an event happens inside of a parent.
 *
 * @param querySelector - a query selector string to use to find the parent element
 * @param child - the child element (like from event.target) to use for checking if child of query item
 *
 * @returns boolean indicating if child is inside of the query
 */
export const containsDomChild = (querySelector, child) => {
    const parent = document.querySelector(querySelector);
    if (parent) {
        return parent.contains(child);
    }
    else {
        new AsperaError('containsDomChild: Unable to find the requested parent', { querySelector });
        return false;
    }
};
/**
 * Clone an object and break all references of it and child object.
 * @remarks used this rarely.  DO NOT USE ON CLASSES.
 *
 * @param objectToClone - the original object to clone
 *
 * @returns a new version of the object.
 */
// eslint-disable-next-line
export const deepClone = (objectToClone) => {
    const response = deepCloneNative(objectToClone);
    if (response === null) {
        new AsperaError('deepClone: Unable to deep clone object', { original: objectToClone });
        return undefined;
    }
    return response;
};
/**
 * Create a safe observable to use as a timeout to take advantage of SubManager.
 *
 * @param time - The time in ms to wait before triggering the observable (default is 1 second)
 *
 * @returns An observable that will essentially be a timeout
 */
export const observableTimeout = (time = 1000) => {
    return of(true).pipe(delay(time));
};
/**
 * Get the current transfer system name
 *
 * @returns human readable string of the transfer system
 */
export const transferSystemName = () => {
    const transferData = getTransferSystemStatus();
    const transferTypes = {
        connect: language.get('modalAppInfo.transferConnect'),
        desktop: language.get('modalAppInfo.transferDesktop'),
        http: language.get('modalAppInfo.transferHttp'),
        mobile: language.get('modalAppInfo.transferMobile'),
        none: language.get('modalAppInfo.none'),
    };
    return transferTypes[transferData.type || 'none'];
};
/**
 * Get the application data for showing to the user
 *
 * @returns - Array of app data to show to user
 */
export const getAppData = () => {
    const transferData = getTransferSystemStatus();
    const userAgent = navigator === null || navigator === void 0 ? void 0 : navigator.userAgent;
    return [
        { id: 'appName', label: language.get('modalAppInfo.appName'), value: getEnvironmentVar('app_name') },
        { id: 'appVersion', label: language.get('modalAppInfo.webVersion'), value: getEnvironmentVar('app_version') },
        { id: 'hash', label: language.get('modalAppInfo.hash'), value: getEnvironmentVar('app_commit_hash') },
        { id: 'libraryVersion', label: language.get('modalAppInfo.libraryVersion'), value: getEnvironmentVar('aspera_starter_version') },
        { id: 'serverVersion', label: language.get('modalAppInfo.serverVersion'), value: getEnvironmentVar('server_version'), hidden: !getEnvironmentVar('server_version') },
        { id: 'currentLanguage', label: language.get('modalAppInfo.currentLanguage'), value: language.getCurrentLanguage().name },
        { id: 'apiLocation', label: language.get('modalAppInfo.apiLocation'), value: getEnvironmentVar('api_url') },
        { id: 'userAgent', label: language.get('modalAppInfo.userAgent'), value: userAgent, hidden: typeof userAgent !== 'string' },
        { id: 'currentTransferSystem', label: language.get('modalAppInfo.currentTransferSystem'), value: transferSystemName() },
        { id: 'connectServer', label: language.get('modalAppInfo.connectServer'), value: getEnvironmentVar('connect_url') || language.get('transferSystem.default') },
        { id: 'connectMinServer', label: language.get('modalAppInfo.connectMinServer'), value: getEnvironmentVar('connect_min_version') || language.get('transferSystem.default') },
        { id: 'connectMethod', label: language.get('modalAppInfo.connectMethod'), value: getEnvironmentVar('connect_method') || language.get('transferSystem.default') },
        { id: 'runningConnectVersion', label: language.get('modalAppInfo.runningConnectVersion'), value: transferData.connectAppVersion, hidden: !transferData.connectAppVersion },
        { id: 'connectStatus', label: language.get('modalAppInfo.connectStatus'), value: transferData.connectStatus, hidden: !transferData.connectStatus },
        { id: 'httpGatewayServer', label: language.get('modalAppInfo.httpGatewayServer'), value: transferData.httpServerUrl },
        { id: 'runningHttpServerVersion', label: language.get('modalAppInfo.runningHttpServerVersion'), value: transferData.httpVersion, hidden: !transferData.httpVersion },
        { id: 'medalliaEnabled', label: language.get('modalAppInfo.medalliaEnabled'), value: (tracking === null || tracking === void 0 ? void 0 : tracking.isNpsRunning) ? language.get('modalAppInfo.yes') : language.get('modalAppInfo.no') },
        { id: 'segmentEnabled', label: language.get('modalAppInfo.segmentEnabled'), value: (tracking === null || tracking === void 0 ? void 0 : tracking.isSegmentRunning) ? language.get('modalAppInfo.yes') : language.get('modalAppInfo.no') },
    ];
};
/**
 * Create a file from data and a file name
 *
 * @param data - any string or object or array to make into a file (if array/object will stringify with 2 spaces)
 * @param type - type of file to create (normally 'text/plain')
 * @param fileName - file name to save as (recommend not using language service; or letting user type in)
 */
export const saveFile = (data, type, fileName) => {
    if (typeof data === 'object' || Array.isArray(data)) {
        try {
            data = JSON.stringify(data, undefined, 2);
        }
        catch (error) {
            new AsperaError('saveFile: encountered error parsing an object type item', error);
        }
    }
    if (typeof window.asperaCustomSave === 'function') {
        window.asperaCustomSave(data, type);
    }
    else {
        const blob = new Blob([data], { type });
        saveAs(blob, fileName);
    }
};
/**
 * Get support data
 * @returns - JSON value
 */
export const getSupportData = () => {
    return {
        app_data: getAppData().filter(item => !item.hidden).map(item => {
            return {
                key: item.label,
                value: item.value,
            };
        }),
        app_errors: (window.asperaLog || []).map(row => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            return {
                message: row.message,
                name: row.date,
                data: (((_c = (_b = (_a = row === null || row === void 0 ? void 0 : row.data) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.data) || ((_e = (_d = row === null || row === void 0 ? void 0 : row.data) === null || _d === void 0 ? void 0 : _d.error) === null || _e === void 0 ? void 0 : _e.response) || ((_g = (_f = row === null || row === void 0 ? void 0 : row.data) === null || _f === void 0 ? void 0 : _f.response) === null || _g === void 0 ? void 0 : _g.data) || ((_h = row === null || row === void 0 ? void 0 : row.data) === null || _h === void 0 ? void 0 : _h.response) || (row === null || row === void 0 ? void 0 : row.data) || row),
            };
        }),
    };
};
/**
 * Download support data to desktop
 */
export const downloadSupportData = () => {
    const data = getSupportData();
    const now = new Date();
    const fileName = `aspera-ui-support-log-${language.getDate(now.toISOString(), 'medium')}.txt`.replace(/,/g, '_');
    if (runningWithAsperaMobile()) {
        try {
            const dataUrl = safeEncode(JSON.stringify(data));
            const url = `data:text/plain;base64,${dataUrl}`;
            openNewTab(url, true);
        }
        catch (error) {
            new AsperaError('downloadSupportData: unable to download data for mobile', error);
        }
        return;
    }
    saveFile(data, 'text/plain', fileName);
};
/**
 * Check if a special key was held during the mouse event. This checks ALT, CTRL
 *
 * @param event - Mouse event
 *
 * @returns boolean indicating if a special key was held
 */
export const altKeysUsed = (event) => {
    return !!(event.altKey || event.ctrlKey);
};
/**
 * Safely zip API calls and handle empty array cases
 *
 * @param apiCalls - Array of API calls; can be empty array
 * @returns An observable that will always resolve even if no calls
 */
export const safeZip = (apiCalls) => {
    return apiCalls.length ? zip(...apiCalls) : of(true);
};
export const supportMobileAppAdvertising = () => {
    return !!(isMobile() && !runningWithAsperaMobile() && getEnvironmentVar('supports_mobile_app') && coreHelpUrls.iosAppStore && coreHelpUrls.androidAppStore);
};
/** Open or install the mobile app */
export const openInstallMobileApp = (openApp) => {
    if (openApp) {
        window.open(`asperamobile://browser/${safeEncode(window.location.href)}`, '_blank', windowOpenSafe);
    }
    else {
        const url = isIOS() ? coreHelpUrls.iosAppStore : coreHelpUrls.androidAppStore;
        if (url) {
            window.open(url, '_blank', windowOpenSafe);
        }
    }
};
/**
 * Remove any paging info from a URL
 * @param url - URL to alter
 * @returns URL without paging info
 */
export const removePagingFromUrl = (url) => {
    const urlParts = url.split('?');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const urlParams = getQueryParams(`?${urlParts[1] || ''}`);
    delete urlParams['offset'];
    delete urlParams['limit'];
    delete urlParams['page'];
    delete urlParams['per_page'];
    return `${urlParts[0]}${getParamString(urlParams)}`;
};
/**
 * Create a recursive setup for getting all items (for page/per_page type calls)
 *
 * @param options - Axios options to get all (DO NOT INCLUDE page or per_page)
 *
 * @returns Observable with an array of items
 */
export const getPageBaseAllApiCall = (options) => {
    const rootUrl = removePagingFromUrl(options.url || '');
    let results = [];
    let page = 1;
    const perPage = 50;
    const getFunction = () => {
        const query = `per_page=${perPage}&page=${page}`;
        options.url = rootUrl + (rootUrl.indexOf('?') > -1 ? `&${query}` : `?${query}`);
        return Axios.request(options).pipe(mergeMap(response => {
            if (Array.isArray(response.data) && response.data.length) {
                results = results.concat(response.data);
                const totalCount = Number(response.headers['x-total-count'] || 0);
                if (results.length && totalCount && totalCount > results.length) {
                    page++;
                    return getFunction();
                }
            }
            return of(results);
        }));
    };
    return getFunction();
};
/**
 * Create a recursive setup for getting all items (for offset/limit type calls)
 *
 * @param options - Axios options to get all (DO NOT INCLUDE offset or limit)
 *
 * @returns Observable with an array of items
 */
export const getOffsetBaseAllApiCall = (options, nextPagingKey) => {
    const rootUrl = removePagingFromUrl(options.url || '');
    let results = [];
    let page = 1;
    const perPage = 50;
    const getFunction = () => {
        const query = `limit=${perPage}&offset=${(page - 1) * perPage}`;
        options.url = rootUrl + (rootUrl.indexOf('?') > -1 ? `&${query}` : `?${query}`);
        return Axios.request(options).pipe(mergeMap(response => {
            var _a;
            let responseData = undefined;
            if (nextPagingKey.indexOf('.') > -1) {
                const keyParts = nextPagingKey.split('.');
                if (keyParts.length === 2 && response.data[keyParts[0]]) {
                    responseData = response.data[keyParts[0]][keyParts[1]];
                }
                if (keyParts.length === 3 && response.data[keyParts[0]] && response.data[keyParts[0]][keyParts[1]]) {
                    responseData = response.data[keyParts[0]][keyParts[1]][keyParts[2]];
                }
            }
            else {
                responseData = response.data[nextPagingKey];
            }
            if (Array.isArray(responseData)) {
                results = results.concat(responseData);
                if (Number(((_a = response.data) === null || _a === void 0 ? void 0 : _a.total_count) || 0) > results.length) {
                    page++;
                    return getFunction();
                }
            }
            return of(results);
        }));
    };
    return getFunction();
};
export const resetTableStore = (resetAll) => {
    if (resetAll) {
        removeStorageItem(storageKeys.tableData);
    }
    else {
        try {
            const currentStore = JSON.parse(getStorageItem(storageKeys.tableData) || '[]');
            if (Array.isArray(currentStore)) {
                currentStore.forEach(item => {
                    item.filterText = '';
                    item.page = 1;
                    item.sort = undefined;
                    item.advancedFilterModel = undefined;
                });
                setStorageItem(storageKeys.tableData, JSON.stringify(currentStore));
            }
        }
        catch (error) {
            new AsperaError('resetTableStore: encountered parsing error', error);
        }
    }
};
/**
 * Use this to setup the table store with predefined or API specific items.
 *
 * This should be set in App componentDidLoad before loading is set to true for the main app.
 * Tracking service will provide the exact string to store on the API and return here.
 *
 * @param data {string version of {id: string, tableSizing?: string}[]} - data to preload for tables. Supported types will be listed as avaialble
 */
export const setTableStoreData = (jsonStringData) => {
    try {
        setStorageItem(storageKeys.tableDataCustomization, jsonStringData);
    }
    catch (error) {
        new AsperaError('setTableStoreData: encountered parsing error', error);
    }
};
/**
 * Parse local table data store and pass data to the Tracking Service.
 * This will be called when user changes data. Be sure to start app using `setTableStoreData` to not lose data.
 */
export const generateTableDataUpdate = () => {
    try {
        const currentStore = JSON.parse(getStorageItem(storageKeys.tableDataCustomization) || '[]');
        if (Array.isArray(currentStore)) {
            const data = currentStore.filter(item => !!(item.id));
            if (data.length) {
                tracking.updateUserPreferences({
                    tableDataStore: JSON.stringify(data),
                });
            }
        }
    }
    catch (error) {
        new AsperaError('generateTableDataUpdate: encountered parsing error', error);
    }
};
/** Handle number input onKeyPress event */
export const numberKeyEvent = (event, min, allowAnyText, allowDecimal) => {
    const key = event === null || event === void 0 ? void 0 : event.key;
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    // Allow COMMAND + A for selecting all and special commands
    if ((event === null || event === void 0 ? void 0 : event.ctrlKey) || (event === null || event === void 0 ? void 0 : event.metaKey) || allowAnyText) {
        return;
    }
    if (min !== undefined && min < 0) {
        allowedKeys.push('-');
    }
    if (allowDecimal) {
        allowedKeys.push('.');
    }
    if (allowedKeys.indexOf(key) === -1 && typeof (event === null || event === void 0 ? void 0 : event.preventDefault) === 'function') {
        event.preventDefault();
    }
};
/** Number translation system for Carbon */
export const numberTranslations = (id) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const translationKeys = {
        'increment.number': 'forms.increment',
        'decrement.number': 'forms.decrement',
    };
    return language.get(translationKeys[id] || 'forms.numberInfo');
};
/**
 * Logout action
 *
 * @param subManager - SubManager from view calling logout
 * @param onClick - Callback after logout
 * @param preLogoutActions - Pre logout cleanup Observable
 */
export const logout = (subManager, onClick, preLogoutActions) => {
    const logoutFlow = () => {
        clearStorageItems();
        onClick();
        if (runningWithAsperaMobile()) {
            sendMobileSdkMessage({
                purpose: 'LOGOUT',
                data: {
                    delay: !!getEnvironmentVar('logout_url'),
                },
            });
        }
        if (getEnvironmentVar('logout_url')) {
            return window.location.replace(getEnvironmentVar('logout_url'));
        }
        window.location.reload();
    };
    subManager.setSubscription((preLogoutActions || of(true)).subscribe({
        next: logoutFlow,
        error: error => {
            new AsperaError('Logout: Unable to perform pre logout actions', error);
            logoutFlow();
        },
    }));
};
/**
 * Get the safe number from a string
 *
 * @param value - Number to try and get clean number
 *
 * @returns the number or 0 if cannot find safe number
 */
export const getSafeNumberValue = (value) => {
    let numberValue = Number(value || '0');
    if (isNaN(numberValue)) {
        numberValue = 0;
    }
    return numberValue;
};
/**
 * Find the best value to use for unit data based on rules from product and design.
 *
 * @param value - number value to find proper unit
 * @param units - unit choices
 *
 * @returns the index and unit to use based on number
 */
export const findBestNumberUnit = (value, units) => {
    let currentUnitIndex = 0;
    let calculatedValue = getSafeNumberValue(value);
    units.forEach((unit, index) => {
        const testValue = getSafeNumberValue(value) / unit.multiplier;
        if (testValue >= 1 && testValue % 1 === 0) {
            currentUnitIndex = index;
            calculatedValue = testValue;
        }
    });
    return {
        unit: units[currentUnitIndex],
        index: currentUnitIndex,
        calculatedValue,
    };
};
/**
 * Get image URI from AsperaImage objects.
 *
 * @param image - Aspera image object
 *
 * @returns - string or undefined for getting an image URI from AsperaImage
 */
export const createImageUrl = (image) => {
    if (image && image.initialData) {
        return image.initialData;
    }
    else if (image && image.type && image.body) {
        return `data:${image.type};base64,${image.body}`;
    }
    return undefined;
};
/**
 * Convert markdown to HTML.  If fails will return raw markdown
 *
 * @param markdown - markdown string
 *
 * @returns - HTML to render on the page
 */
export const markdownToHtml = (markdown) => {
    try {
        const converter = new MarkdownIt();
        let htmlData = converter.render(markdown);
        htmlData = htmlData.replace(/<script/gi, '').replace(/<\/script>/gi, '').replace(/javascript:/gi, '');
        return htmlData;
    }
    catch (error) {
        new AsperaError('markdownToHtml: unable to convert markdown', error);
        return markdown;
    }
};
export const nodeTransferToConnectTransfer = (nodeTransfer) => {
    var _a, _b, _c, _d, _e, _f, _g;
    let allSessionsTotal = 0;
    let calcRateKbps = 0;
    const session = ((_a = nodeTransfer.sessions) === null || _a === void 0 ? void 0 : _a.length) ? nodeTransfer.sessions[0] : undefined;
    const bytesWritten = nodeTransfer.bytes_written || 0;
    const bytesExpected = ((_b = nodeTransfer.precalc) === null || _b === void 0 ? void 0 : _b.bytes_expected) || 0;
    const remainingKiloBytes = (bytesExpected - bytesWritten) / 1000;
    (_c = nodeTransfer.sessions) === null || _c === void 0 ? void 0 : _c.forEach(session => {
        allSessionsTotal += session.calc_rate_kbps;
    });
    if (((_d = nodeTransfer.sessions) === null || _d === void 0 ? void 0 : _d.length) && allSessionsTotal) {
        calcRateKbps = allSessionsTotal / nodeTransfer.sessions.length;
    }
    const timeRemaining = Math.round(remainingKiloBytes / (calcRateKbps * 8)) * 100;
    return {
        uuid: nodeTransfer.id || '',
        desktopSdkTransfer: false,
        transfer_spec: nodeTransfer.start_spec || {},
        add_time: nodeTransfer.start_time_usec ? new Date((nodeTransfer.start_time_usec || 1) / 1000).toISOString() : '',
        current_file: '',
        file_counts: {
            attempted: 0,
            completed: nodeTransfer.files_completed || 0,
            failed: ((_e = nodeTransfer.precalc) === null || _e === void 0 ? void 0 : _e.files_failed) || 0,
            skipped: ((_f = nodeTransfer.precalc) === null || _f === void 0 ? void 0 : _f.files_excluded) || 0,
        },
        end_time: nodeTransfer.end_time_usec ? new Date((nodeTransfer.end_time_usec || 1) / 1000).toISOString() : '',
        transport: 'fasp',
        status: nodeTransfer.status || 'queued',
        error_desc: nodeTransfer.error_desc || '',
        error_code: nodeTransfer.error_code || 0,
        bytes_written: bytesWritten,
        bytes_expected: bytesExpected,
        files: (_g = nodeTransfer.files) === null || _g === void 0 ? void 0 : _g.map(file => {
            return {
                name: file,
                size: 0,
            };
        }),
        calculated_rate_kbps: calcRateKbps,
        elapsed_usec: nodeTransfer.elapsed_usec || 0,
        percentage: (bytesExpected && bytesWritten) ? (bytesWritten / bytesExpected) * 100 : 0,
        remaining_usec: timeRemaining * 1000000,
        title: nodeTransfer.title || '',
        target_rate_kbps: (session === null || session === void 0 ? void 0 : session.target_rate_kbps) || undefined,
    };
};
/**
 * Get a User friendly string representing time from seconds
 *
 * @param remainingSeconds - Seconds to calculate on
 *
 * @returns String to show on the UI
 */
export const getTimeString = (remainingSeconds) => {
    if (!remainingSeconds || remainingSeconds < 0) {
        return '';
    }
    const days = Math.floor(remainingSeconds / 86400);
    const hours = Math.floor(remainingSeconds % 86400 / 3600);
    const minutes = Math.floor(remainingSeconds % 3600 / 60);
    const seconds = Math.floor(remainingSeconds % 60);
    const stringParts = [];
    if (days === 1)
        stringParts.push(language.get('transferActivity.oneDay'));
    if (days && days !== 1)
        stringParts.push(language.get('transferActivity.itemDays', [days]));
    if (hours === 1)
        stringParts.push(language.get('transferActivity.oneHour'));
    if (hours && hours !== 1)
        stringParts.push(language.get('transferActivity.itemHours', [hours]));
    if (minutes === 1)
        stringParts.push(language.get('transferActivity.oneMin'));
    if (minutes && minutes !== 1)
        stringParts.push(language.get('transferActivity.itemMins', [minutes]));
    if (seconds)
        stringParts.push(language.get('transferActivity.itemSeconds', [seconds]));
    return stringParts.join(' ');
};
const exports = {
    getStorageItem,
    getJsonStorageItem,
    setStorageItem,
    setJsonStorageItem,
    removeStorageItem,
    clearStorageItems,
    openNewTab,
    containsDomChild,
    deepClone,
    observableTimeout,
    saveFile,
    downloadSupportData,
    getSupportData,
    altKeysUsed,
    safeZip,
    supportMobileAppAdvertising,
    openInstallMobileApp,
    getPageBaseAllApiCall,
    getOffsetBaseAllApiCall,
    removePagingFromUrl,
    resetTableStore,
    setTableStoreData,
    generateTableDataUpdate,
    numberKeyEvent,
    numberTranslations,
    logout,
    getSafeNumberValue,
    findBestNumberUnit,
    createImageUrl,
    nodeTransferToConnectTransfer,
    getTimeString,
};
export default exports;
